import { useState } from 'react';
import { X } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { LoadFields, type LoadFieldsValue } from './LoadFields';
import type { NurseryDeliveryZone, NurseryLoad } from '../../../types/nursery';

/**
 * Make a load, optionally with orders already on it (from the planner's
 * selection). The caller works out sensible defaults from what was selected.
 */

export interface NewLoadDialogProps {
  initial: LoadFieldsValue;
  orderIds: number[];
  trees: number;
  farms: { id: number; name: string; picker_count?: number }[];
  zones: NurseryDeliveryZone[];
  onClose: () => void;
  onCreated: (load: NurseryLoad) => void;
}

export function NewLoadDialog({ initial, orderIds, trees, farms, zones, onClose, onCreated }: NewLoadDialogProps) {
  const [value, setValue] = useState<LoadFieldsValue>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    if (!value.despatch_farm_id || !value.despatch_date) {
      setError('Choose a despatch farm and a date.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await nurseryService.createLoad({ ...value, order_ids: orderIds.length ? orderIds : undefined });
      if (res.success) onCreated(res.data);
      else setError(errorMessage(res, 'Could not create the load'));
    } catch (e) {
      setError(errorMessage(e, 'Could not create the load'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="new-load-title">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            <h2 id="new-load-title" className="text-lg font-semibold text-neutral-900">New load</h2>
            <p className="text-sm text-neutral-500">
              {orderIds.length
                ? `${orderIds.length} order${orderIds.length === 1 ? '' : 's'}, ${trees.toLocaleString()} trees`
                : 'Empty. Add orders from the despatch planner.'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>
        <div className="px-5 py-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">{error}</div>}
          <LoadFields value={value} onChange={setValue} farms={farms} zones={zones} />
        </div>
        <div className="flex flex-wrap justify-end gap-2 px-5 pb-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={create} disabled={saving}>{saving ? 'Creating…' : 'Create load'}</Button>
        </div>
      </div>
    </div>
  );
}

export default NewLoadDialog;
